import { useState } from "react";
import { Dialog, Disclosure, Popover } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import logo from "../assets/logo.png";
import { Link } from "react-router-dom";
import { service } from "../Data/data";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Header({ setShow, setOpen, show }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Prevent flickering
  let hoverTimeout;
  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout);
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    hoverTimeout = setTimeout(() => setIsHovered(false), 100);
  };

  const products = [
    { navName: "Web Development", to: `/service/${service[0]?.slug}` },
    { navName: "App Development", to: `/service/${service[1]?.slug}` },
    { navName: "Digital Marketing", to: `/service/${service[2]?.slug}` },
    { navName: "Growth Strategy", to: `/service/${service[3]?.slug}` },
    { navName: "Market Research", to: `/service/${service[4]?.slug}` },
    { navName: "Business Strategy", to: `/service/${service[5]?.slug}` },
    { navName: "IT Solutions", to: `/service/${service[7]?.slug}` },
  ];

  return (
    <header className="bg-white sticky top-0 z-50 shadow-md">
      <nav
        className="mx-auto flex max-w-7xl h-[70px] items-center justify-between px-6 lg:px-12"
        aria-label="Global"
      >
        {/* Logo */}
        <div className="flex items-center flex-shrink-0">
          <Link
            to="/"
            onClick={() => {
              setOpen(false);
              setShow(false);
            }}
          >
            <img
              className="h-[55px] w-auto object-contain"
              src={logo}
              alt="logo"
            />
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex lg:hidden">
          <button
            type="button"
            className="p-2 text-gray-700"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Bars3Icon className="h-7 w-7" />
          </button>
        </div>

        {/* Desktop Nav */}
        <Popover.Group className="hidden lg:flex lg:gap-x-10 items-center">
          <Link
            to="/"
            className="text-sm font-medium text-gray-900 hover:text-blue-600"
            onClick={() => {
              setOpen(false);
              setShow(false);
            }}
          >
            Home
          </Link>

          {/* Services Dropdown */}
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex cursor-pointer items-center gap-x-1 text-sm font-medium text-gray-900 hover:text-blue-600">
              <Link to="/services">Services</Link>
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            </div>

            {isHovered && (
              <div className="absolute left-0 top-full mt-2 w-[300px] rounded-xl bg-white shadow-lg ring-1 ring-gray-200">
                <div className="p-3">
                  {products.map((item) => (
                    <Link
                      key={item.navName}
                      to={item.to}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                      onClick={() => setIsHovered(false)}
                    >
                      {item.navName}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/about"
            className="text-sm font-medium text-gray-900 hover:text-blue-600"
          >
            About Us
          </Link>
          <Link
            to="/pricelist"
            className="text-sm font-medium text-gray-900 hover:text-blue-600"
          >
            Pricing
          </Link>
          <Link
            to="/portfolio"
            className="text-sm font-medium text-gray-900 hover:text-blue-600"
          >
            Portfolio
          </Link>
          <Link
            to="/blog"
            className="text-sm font-medium text-gray-900 hover:text-blue-600"
          >
            Blog
          </Link>
          <Link
            to="/support"
            className="text-sm font-medium text-gray-900 hover:text-blue-600"
          >
            Support
          </Link>
        </Popover.Group>

        {/* CTA Button */}
        <div className="hidden lg:flex">
          <Link
            className="ml-8 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition"
            onClick={() => {
              setOpen(false);
              setShow(true);
            }}
          >
            Get A Call Back →
          </Link>
        </div>
      </nav>

      {/* Mobile Menu */}
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-72 bg-white px-6 py-6 shadow-lg">
          <div className="flex items-center justify-between">
            <Link to="/" onClick={() => setMobileMenuOpen(false)}>
              <img className="h-12 w-auto" src={logo} alt="logo" />
            </Link>
            <button
              type="button"
              className="p-2 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-6 space-y-4">
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100">
                    Services
                    <ChevronDownIcon
                      className={classNames(
                        open ? "rotate-180" : "",
                        "h-5 w-5 text-gray-500"
                      )}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="pl-6 space-y-2">
                    {products.map((item, index) => (
                      <Link
                        key={index}
                        to={item.to}
                        className="block rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.navName}
                      </Link>
                    ))}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>

            <Link
              to="/"
              className="block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>
            <Link
              to="/pricelist"
              className="block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <Link
              to="/portfolio"
              className="block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              Portfolio
            </Link>
            <Link
              to="/blog"
              className="block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Link
              to="/support"
              className="block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              Support
            </Link>

            <Link
            className="ml-8 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition"
            onClick={() => {
              setOpen(false);
              setMobileMenuOpen(false)
              setShow(true);
            }}
          >
            Get A Call Back →
          </Link>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}
